import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppNav from '../components/AppNav';
import { apiClient } from '../api';
import { BellOff, MapPin, User, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const Muted = () => {
  const [muted, setMuted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/notifications/muted');
      setMuted(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const unmute = async (u) => {
    setBusy((b) => ({ ...b, [u.user_id]: true }));
    try {
      await apiClient.delete(`/notifications/mute/${u.user_id}`);
      setMuted((prev) => prev.filter((m) => m.user_id !== u.user_id));
      toast(`Unmuted ${u.name}`);
    } catch (e) {
      toast.error('Could not unmute');
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[u.user_id]; return n; });
    }
  };

  return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-8">
        <Link to="/me" className="inline-flex items-center text-purple-200 hover:text-white text-sm mb-5">
          <ArrowLeft className="w-4 h-4 mr-1.5" />Back to my profile
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <BellOff className="w-7 h-7 text-fnl-pink" />
          <h1 className="text-3xl font-extrabold text-white">Muted users</h1>
        </div>
        <p className="text-purple-300 text-sm mb-6">
          You won&apos;t get push notifications about messages or profile views from anyone listed here.
          Tap <span className="text-white font-semibold">Unmute</span> on a user to start getting alerts from them again.
        </p>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-fnl-pink border-t-transparent rounded-full animate-spin" />
          </div>
        ) : muted.length === 0 ? (
          <div className="bg-fnl-purple/60 rounded-2xl p-10 border border-purple-900/40 text-center" data-testid="muted-empty">
            <BellOff className="w-12 h-12 text-fnl-pink/40 mx-auto mb-3" />
            <p className="text-white font-semibold mb-1">No one muted</p>
            <p className="text-purple-300 text-sm">Visit any profile and tap &ldquo;Mute notifications from this person&rdquo; to silence them.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="muted-list">
            {muted.map((u) => (
              <li
                key={u.user_id}
                data-testid={`muted-${u.user_id}`}
                className="flex items-center gap-3 bg-fnl-purple/60 rounded-xl p-3 border border-purple-900/40"
              >
                <Link to={`/profile/${u.user_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-fnl-pink/25 overflow-hidden flex items-center justify-center shrink-0">
                    {u.photo_base64 ? (
                      <img src={u.photo_base64} alt={u.name} className="w-full h-full object-cover" />
                    ) : <User className="w-6 h-6 text-fnl-pink" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold truncate">
                      {u.name}{u.age ? <>, <span className="text-fnl-pink">{u.age}</span></> : null}
                    </div>
                    {u.location && (
                      <div className="text-xs text-purple-300 truncate flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{u.location}
                      </div>
                    )}
                  </div>
                </Link>
                <Button
                  onClick={() => unmute(u)}
                  disabled={!!busy[u.user_id]}
                  variant="outline"
                  className="bg-fnl-purple-light/40 border-purple-700 text-white hover:bg-fnl-pink hover:border-fnl-pink hover:text-white shrink-0"
                  data-testid={`muted-unmute-${u.user_id}`}
                >
                  Unmute
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Muted;
